/** Offer list with status filters. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { OfferResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { apiGetPage } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import {
  DataList,
  FilterTabs,
  SearchBox,
  useUrlFilters,
} from "../../components/ui/controls.js";
import {
  ErrorPanel,
  ExportCsvLink,
  LoadingPanel,
  formatDate,
  formatMoney,
} from "../shared.js";

export const LIST_STATUSES = [
  "",
  "draft",
  "sent",
  "accepted",
  "declined",
  "expired",
] as const;

export function OffersListPage() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const { values, page, set } = useUrlFilters(["status", "q"] as const);
  const pageSize = 25;
  const query = useQuery({
    queryKey: ["offers", values, page],
    queryFn: () =>
      apiGetPage<OfferResponse>("/offers", {
        status: values.status || undefined,
        q: values.q || undefined,
        page,
        pageSize,
      }),
    placeholderData: (previous) => previous,
  });
  return (
    <>
      <PageHeader
        title={t("offer.plural")}
        subtitle={t("offer.listSubtitle")}
        actions={
          <>
            <ExportCsvLink
              dataset="offers"
              query={{ status: values.status || undefined, q: values.q }}
            />
            {has("offers.create") ? (
              <Button as={Link} to="/offers/new" icon="pi pi-plus">
                {t("offer.new")}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FilterTabs
          value={values.status as (typeof LIST_STATUSES)[number]}
          onChange={(status) => set({ status })}
          options={LIST_STATUSES.map((value) => ({
            value,
            label: value ? t(`status.${value}`) : t("common.all"),
          }))}
        />
        <SearchBox value={values.q} onChange={(q) => set({ q })} />
      </div>
      {query.error ? (
        <ErrorPanel error={query.error} />
      ) : !query.data ? (
        <LoadingPanel />
      ) : (
        <DataList
          rows={query.data.data}
          rowKey={(offer) => offer.id}
          href={(offer) => `/offers/${offer.id}`}
          page={page}
          pageSize={pageSize}
          total={query.data.total}
          onPage={(next) => set({ page: next })}
          loading={query.isFetching || undefined}
          empty={
            values.q || values.status ? t("common.noMatches") : t("offer.empty")
          }
          columns={[
            {
              key: "title",
              header: t("common.title"),
              cell: (offer) => offer.title,
            },
            {
              key: "number",
              header: t("common.number"),
              cell: (offer) => (
                <span className="font-mono text-xs">{offer.displayNumber}</span>
              ),
            },
            {
              key: "customer",
              header: t("common.customer"),
              cell: (offer) => offer.customer.displayName,
            },
            {
              key: "validUntil",
              header: t("offer.validUntil"),
              cell: (offer) => formatDate(offer.validUntil),
              hideOnMobile: true,
            },
            {
              key: "total",
              header: t("totals.total"),
              align: "right",
              cell: (offer) => formatMoney(offer.grandTotal, offer.currency),
            },
            {
              key: "status",
              header: t("common.status"),
              cell: (offer) => <StatusBadge status={offer.status} />,
            },
          ]}
        />
      )}
    </>
  );
}
